/**
 * Session-scoped storage for frontend-only patient tags.
 */

import type { Patient } from '../types';

const PATIENT_TAGS_STORAGE_KEY = 'clinical-trials:patient-tags:v1';

type StoredPatientTags = Record<string, string[]>;

/**
 * Read all patient tags from session storage.
 *
 * @param storage - Browser storage implementation, injectable for tests.
 * @returns Patient tags keyed by MRN.
 */
export function readStoredPatientTags(storage: Storage = window.sessionStorage): StoredPatientTags {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(PATIENT_TAGS_STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string[]] =>
          Array.isArray(entry[1]) && entry[1].every((tag) => typeof tag === 'string'),
      ),
    );
  } catch {
    return {};
  }
}

/**
 * Persist one patient's de-duplicated tags for the current browser session.
 *
 * @param mrn - Patient medical record number.
 * @param tags - Replacement patient tags.
 * @param storage - Browser storage implementation, injectable for tests.
 * @returns Nothing.
 */
export function writeStoredPatientTags(mrn: string, tags: string[], storage: Storage = window.sessionStorage): void {
  const storedTags = readStoredPatientTags(storage);
  const normalizedTags = Array.from(new Set(tags.map((tag) => tag.trim()).filter(Boolean)));

  if (normalizedTags.length > 0) {
    storedTags[mrn] = normalizedTags;
  } else {
    delete storedTags[mrn];
  }

  try {
    storage.setItem(PATIENT_TAGS_STORAGE_KEY, JSON.stringify(storedTags));
  } catch {
    // Tags remain usable in component state when browser storage is unavailable.
  }
}

/**
 * Overlay session tags onto freshly fetched patient rows.
 *
 * @param patients - Notification patients returned by the API transformer.
 * @param storage - Browser storage implementation, injectable for tests.
 * @returns Patients with session tags applied when present.
 */
export function mergeStoredPatientTags(patients: Patient[], storage: Storage = window.sessionStorage): Patient[] {
  const storedTags = readStoredPatientTags(storage);
  return patients.map((patient) => ({
    ...patient,
    tags: storedTags[patient.mrn] ?? patient.tags ?? [],
  }));
}
